
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { Lesson } from '@/models/Lesson';
import { format } from 'date-fns';
import LevelDonut from './LevelDonut';
import { useAuth } from '@/contexts/AuthContext';

interface LessonSelectionProps {
  activeLessons: Lesson[];
  onSelectLesson: (lesson: Lesson) => void;
}

const LessonSelection = ({ activeLessons, onSelectLesson }: LessonSelectionProps) => {
  const { user } = useAuth();
  const formatLastCompleted = (date: any) => {
    if (!date) return 'Never completed';
    if (date.toDate) {
      // Handle Firestore Timestamp
      return format(date.toDate(), 'MMM d, yyyy');
    }
    return format(new Date(date), 'MMM d, yyyy');
  };

  return (
    <div className="space-y-6">
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Choose a lesson</CardTitle>
          <CardDescription>Select a lesson to practice</CardDescription>
        </CardHeader>
        <CardContent>
          {activeLessons.length === 0 ? (
            <div className="text-center py-8 px-4 rounded-lg border border-dashed border-border">
              <p className="text-muted-foreground mb-4">No active lessons available</p>
              <Link to="/home">
                <Button variant="outline" size="sm">
                  Return to Home
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {activeLessons.map(lesson => (
                <div 
                  key={lesson.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-card border border-border transition-all hover:shadow-md cursor-pointer"
                  onClick={() => onSelectLesson(lesson)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{lesson.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-muted-foreground">{lesson.words.length} words</span>
                      {lesson.lastCompletedAt && (
                        <div className="flex items-center text-xs gap-1 text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>Last: {formatLastCompleted(lesson.lastCompletedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {user && <LevelDonut lessonId={lesson.id} userId={user.id} />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LessonSelection;
